/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, CalendarMonth, Edit } from "@mui/icons-material";
import moment from "moment";
import { tableStyle } from "../../components";
import { CssTextField } from "../../components/TextField";
import Header from "../../components/Header";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import {
  ACTIVATE_BOOKING_ENGINE_DISCOUNT,
  ACTIVATE_ROOMTYPE_OFFER,
  ADD_NEW_BOOKING_ENGINE_DISCOUNT,
  ADD_ROOMTYPE_OFFER,
  LIST_ALL_BOOKING_ENGINE_ROOM_TYPE_DISCOUNTS,
  LIST_ALL_ROOMTYPE_BOOKING_ENGINE_OFFERS,
  LIST_BOOKING_ENGINE_ROOM_TYPE_DISCOUNT_DETAILS,
  UPDATE_BOOKING_ENGINE_DISCOUNT,
  UPDATE_ROOMTYPE_OFFER,
} from "../../graphql";
import { authorizedPost } from "../../store/actions";

const startIcon = {
  startAdornment: (
    <InputAdornment position="start">
      <CalendarMonth fontSize="small" sx={{ cursor: "pointer" }} />
    </InputAdornment>
  ),
};

const rateTypes = [
  { label: "Seasonal", value: "booking_engine_seasonal" },
  { label: "Weekdays", value: "booking_engine_weekdays" },
];

function RoomtypeBookingEngine({ roomtypeId, dispatch, colors, roomtypeDetails }) {
  const startInputRef = useRef();
  const endInputRef = useRef();
  const discountStartInputRef = useRef();
  const discountEndInputRef = useRef();

  const [rates, setRates] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [isSubmittingRate, setIsSubmittingRate] = useState(false);
  const [isSubmittingDiscount, setIsSubmittingDiscount] = useState(false);

  const [openRateModal, setOpenRateModal] = useState(false);
  const [openDiscountModal, setOpenDiscountModal] = useState(false);

  const [editingRateId, setEditingRateId] = useState("");
  const [editingDiscountId, setEditingDiscountId] = useState("");

  const [rateValues, setRateValues] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(1, "days").format("YYYY-MM-DD"),
    type: "booking_engine_seasonal",
    weekdays: [],
    singleOccupancyRate: "",
    standardRate: "",
    maxOccupancyRate: "",
    extraBedRate: "",
    childRate: "",
  });

  const [discountValues, setDiscountValues] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().add(1, "days").format("YYYY-MM-DD"),
    mode: "percentage",
    discountValue: "",
    fixedRate: "",
  });

  const modalStyle = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { sm: 520, xs: "92%" },
      bgcolor: colors.primary[400],
      outline: "none",
      boxShadow: 24,
      p: 2,
      borderRadius: 2,
      maxHeight: "90vh",
      overflowY: "auto",
    }),
    [colors]
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [offersResponse, discountsResponse] = await Promise.all([
      authorizedPost(LIST_ALL_ROOMTYPE_BOOKING_ENGINE_OFFERS, {
        roomtypeId,
        pageNumber: 1,
        pageSize: 100,
      }),
      authorizedPost(LIST_ALL_BOOKING_ENGINE_ROOM_TYPE_DISCOUNTS, {
        roomtypeId,
        pageNumber: 1,
        pageSize: 100,
      }),
    ]);

    setRates(offersResponse?.data?.list_all_roomtype_booking_engine_offers?.data || []);
    setDiscounts(discountsResponse?.data?.list_all_booking_engine_room_type_discounts?.data || []);
  };

  const resetRateForm = () => {
    setEditingRateId("");
    setRateValues({
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().add(1, "days").format("YYYY-MM-DD"),
      type: "booking_engine_seasonal",
      weekdays: [],
      singleOccupancyRate: "",
      standardRate: "",
      maxOccupancyRate: "",
      extraBedRate: "",
      childRate: "",
    });
  };

  const resetDiscountForm = () => {
    setEditingDiscountId("");
    setDiscountValues({
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().add(1, "days").format("YYYY-MM-DD"),
      mode: "percentage",
      discountValue: "",
      fixedRate: "",
    });
  };

  const openRateForEdit = (row) => {
    setEditingRateId(row?._id || "");
    setRateValues({
      startDate: row?.start_date ? moment.unix(row.start_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      endDate: row?.end_date ? moment.unix(row.end_date).format("YYYY-MM-DD") : moment().add(1, "days").format("YYYY-MM-DD"),
      type: row?.type || "booking_engine_seasonal",
      weekdays: row?.weekdays || [],
      singleOccupancyRate: row?.single_occupancy_rate ?? "",
      standardRate: row?.standard_rate ?? "",
      maxOccupancyRate: row?.max_occupancy_rate ?? "",
      extraBedRate: row?.extra_bed_rate ?? "",
      childRate: row?.child_rate ?? "",
    });
    setOpenRateModal(true);
  };

  const openDiscountForEdit = async (id) => {
    const detailsResponse = await authorizedPost(
      LIST_BOOKING_ENGINE_ROOM_TYPE_DISCOUNT_DETAILS,
      { id }
    );
    const data = detailsResponse?.data?.list_booking_engine_room_type_discount_details?.data;
    if (!data?._id) return;

    setEditingDiscountId(data._id);
    setDiscountValues({
      startDate: data?.start_date ? moment.unix(data.start_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      endDate: data?.end_date ? moment.unix(data.end_date).format("YYYY-MM-DD") : moment().add(1, "days").format("YYYY-MM-DD"),
      mode: data?.is_rate_fixed ? "fixed" : "percentage",
      discountValue: data?.percentage ?? "",
      fixedRate: data?.fixed_rate ?? "",
    });
    setOpenDiscountModal(true);
  };

  const handleSubmitRate = async (e) => {
    e.preventDefault();
    setIsSubmittingRate(true);

    const payload = {
      roomtypeId,
      startDate: moment(rateValues.startDate, "YYYY-MM-DD").unix(),
      endDate: moment(rateValues.endDate, "YYYY-MM-DD").unix(),
      type: rateValues.type,
      weekdays: rateValues.type === "booking_engine_weekdays" ? rateValues.weekdays : [],
      isRateFixed: true,
      fixedRate: 0,
      percentage: 0,
      isRateIncrement: true,
      singleOccupancyRate: Number(rateValues.singleOccupancyRate || 0),
      standardRate: Number(rateValues.standardRate || 0),
      maxOccupancyRate: Number(rateValues.maxOccupancyRate || 0),
      extraBedRate: Number(rateValues.extraBedRate || 0),
      childRate: Number(rateValues.childRate || 0),
    };

    const mutation = editingRateId ? UPDATE_ROOMTYPE_OFFER : ADD_ROOMTYPE_OFFER;
    await authorizedPost(mutation, editingRateId ? { ...payload, id: editingRateId } : payload);

    await fetchData();
    setOpenRateModal(false);
    resetRateForm();
    setIsSubmittingRate(false);
  };

  const handleSubmitDiscount = async (e) => {
    e.preventDefault();
    setIsSubmittingDiscount(true);

    const isFixedRate = discountValues.mode === "fixed";
    const commonPayload = {
      roomtypeId,
      startDate: moment(discountValues.startDate, "YYYY-MM-DD").unix(),
      endDate: moment(discountValues.endDate, "YYYY-MM-DD").unix(),
    };

    if (editingDiscountId) {
      await authorizedPost(UPDATE_BOOKING_ENGINE_DISCOUNT, {
        ...commonPayload,
        id: editingDiscountId,
        isRateFixed: isFixedRate,
        percentage: isFixedRate ? 0 : Number(discountValues.discountValue || 0),
        fixedRate: isFixedRate ? Number(discountValues.fixedRate || 0) : 0,
      });
    } else {
      await authorizedPost(ADD_NEW_BOOKING_ENGINE_DISCOUNT, {
        ...commonPayload,
        isRateFixed: isFixedRate,
        percentage: isFixedRate ? 0 : Number(discountValues.discountValue || 0),
        fixedRate: isFixedRate ? Number(discountValues.fixedRate || 0) : 0,
      });
    }

    await fetchData();
    setOpenDiscountModal(false);
    resetDiscountForm();
    setIsSubmittingDiscount(false);
  };

  const rateColumns = [
    {
      field: "start_date",
      headerName: "Start Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.start_date ? moment.unix(row.start_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.end_date ? moment.unix(row.end_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "type",
      headerName: "Offer Type",
      width: 180,
      renderCell: ({ row: { type } }) =>
        type === "booking_engine_weekdays" ? "Weekdays" : "Seasonal",
    },
    {
      field: "weekdays",
      headerName: "Weekdays",
      width: 180,
      renderCell: ({ row: { weekdays } }) =>
        weekdays?.length ? weekdays.join(", ") : "-",
    },
    {
      field: "single_occupancy_rate",
      headerName: "Single",
      width: 90,
      renderCell: ({ row }) => row?.single_occupancy_rate ?? "-",
    },
    {
      field: "standard_rate",
      headerName: "Double",
      width: 90,
      renderCell: ({ row }) => row?.standard_rate ?? "-",
    },
    {
      field: "max_occupancy_rate",
      headerName: "Triple",
      width: 90,
      renderCell: ({ row }) => row?.max_occupancy_rate ?? "-",
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: ({ row: { is_active, _id } }) => (
        <AvailabilityUpdation
          isActive={is_active}
          action={async () => {
            await authorizedPost(ACTIVATE_ROOMTYPE_OFFER, { id: _id });
            fetchData();
          }}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit fontSize="small" />}
          onClick={() => openRateForEdit(row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const discountColumns = [
    {
      field: "start_date",
      headerName: "Start Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.start_date ? moment.unix(row.start_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 130,
      valueGetter: ({ row }) =>
        row?.end_date ? moment.unix(row.end_date).format("DD-MMM-YYYY") : "-",
    },
    {
      field: "discount_type",
      headerName: "Type",
      width: 120,
      renderCell: ({ row }) => (row?.is_rate_fixed ? "Fixed Rate" : "Percentage"),
    },
    {
      field: "discount_value",
      headerName: "Value",
      width: 120,
      renderCell: ({ row }) =>
        row?.is_rate_fixed
          ? row?.fixed_rate ?? "-"
          : `${row?.percentage ?? 0}%`,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: ({ row: { is_active, _id } }) => (
        <AvailabilityUpdation
          isActive={is_active}
          action={async () => {
            await authorizedPost(ACTIVATE_BOOKING_ENGINE_DISCOUNT, { id: _id });
            fetchData();
          }}
        />
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 100,
      renderCell: ({ row: { _id } }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Edit fontSize="small" />}
          onClick={() => openDiscountForEdit(_id)}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box mb={15}>
      <Stack spacing={4}>
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="end"
            mb={1.5}
          >
            <Typography variant="h4" fontWeight={700}>
              BOOKING ENGINE RATES
            </Typography>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: { sm: "10px 20px", xs: "5px 10px" },
              }}
              onClick={() => setOpenRateModal(true)}
            >
              <Add sx={{ mr: "10px" }} />
              Add new rate
            </Button>
          </Stack>
          {/* <Typography variant="body2" color={colors.grey[300]} mb={1.5}>
            Offer types: booking_engine_weekdays, booking_engine_seasonal
          </Typography> */}
          <Box height="350px" sx={tableStyle(colors)}>
            <DataGrid rows={rates} columns={rateColumns} getRowId={(row) => row?._id} />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="end"
            mb={1.5}
          >
            <Typography variant="h4" fontWeight={700}>
              BOOKING ENGINE DISCOUNTS
            </Typography>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: { sm: "10px 20px", xs: "5px 10px" },
              }}
              onClick={() => setOpenDiscountModal(true)}
            >
              <Add sx={{ mr: "10px" }} />
              Add new discount
            </Button>
          </Stack>
          <Box height="350px" sx={tableStyle(colors)}>
            <DataGrid
              rows={discounts}
              columns={discountColumns}
              getRowId={(row) => row?._id}
            />
          </Box>
        </Box>
      </Stack>

      <Modal
        open={openRateModal}
        onClose={() => {
          setOpenRateModal(false);
          resetRateForm();
        }}
      >
        <Box sx={modalStyle}>
          <Header
            title={editingRateId ? "UPDATE BOOKING ENGINE RATE" : "ADD BOOKING ENGINE RATE"}
            titleVariant="h4"
          />
          <form onSubmit={handleSubmitRate}>
            <Stack spacing={2}>
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="Start date"
                name="startDate"
                value={moment(rateValues.startDate).format("YYYY-MM-DD")}
                inputProps={{
                  min: moment().format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                onChange={(e) =>
                  setRateValues((prev) => ({ ...prev, startDate: e.target.value }))
                }
                inputRef={startInputRef}
                onClick={() => startInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="End date"
                name="endDate"
                value={moment(rateValues.endDate).format("YYYY-MM-DD")}
                inputProps={{
                  min: moment(rateValues.startDate).format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                onChange={(e) =>
                  setRateValues((prev) => ({ ...prev, endDate: e.target.value }))
                }
                inputRef={endInputRef}
                onClick={() => endInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                select
                size="small"
                label="Offer type"
                value={rateValues.type}
                onChange={(e) =>
                  setRateValues((prev) => ({ ...prev, type: e.target.value }))
                }
              >
                {rateTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </CssTextField>

              {rateValues.type === "booking_engine_weekdays" && (
                <CssTextField
                  fullWidth
                  select
                  size="small"
                  label="Weekdays"
                  value={rateValues.weekdays}
                  SelectProps={{ multiple: true }}
                  onChange={(e) =>
                    setRateValues((prev) => ({ ...prev, weekdays: e.target.value }))
                  }
                >
                  {moment.weekdays().map((day) => (
                    <MenuItem key={day} value={day}>
                      <Checkbox
                        sx={{ p: "0 5px" }}
                        checked={rateValues.weekdays.indexOf(day) > -1}
                      />
                      {day}
                    </MenuItem>
                  ))}
                </CssTextField>
              )}

              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Single occupancy rate"
                value={rateValues.singleOccupancyRate}
                onChange={(e) =>
                  setRateValues((prev) => ({
                    ...prev,
                    singleOccupancyRate: e.target.value,
                  }))
                }
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Double occupancy rate"
                value={rateValues.standardRate}
                onChange={(e) =>
                  setRateValues((prev) => ({ ...prev, standardRate: e.target.value }))
                }
              />
              {roomtypeDetails?.flexible_occupancy ? (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Extra bed rate"
                  value={rateValues.extraBedRate}
                  onChange={(e) =>
                    setRateValues((prev) => ({ ...prev, extraBedRate: e.target.value }))
                  }
                />
              ) : (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Triple occupancy rate"
                  value={rateValues.maxOccupancyRate}
                  onChange={(e) =>
                    setRateValues((prev) => ({
                      ...prev,
                      maxOccupancyRate: e.target.value,
                    }))
                  }
                />
              )}
              <CssTextField
                fullWidth
                required
                size="small"
                type="number"
                label="Child rate"
                value={rateValues.childRate}
                onChange={(e) =>
                  setRateValues((prev) => ({ ...prev, childRate: e.target.value }))
                }
              />
              <Button fullWidth type="submit" color="secondary" variant="contained">
                {isSubmittingRate ? (
                  <CircularProgress color="inherit" size={22} />
                ) : (
                  <b>{editingRateId ? "Update" : "Submit"}</b>
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>

      <Modal
        open={openDiscountModal}
        onClose={() => {
          setOpenDiscountModal(false);
          resetDiscountForm();
        }}
      >
        <Box sx={modalStyle}>
          <Header
            title={
              editingDiscountId
                ? "UPDATE BOOKING ENGINE DISCOUNT"
                : "ADD BOOKING ENGINE DISCOUNT"
            }
            titleVariant="h4"
          />
          <form onSubmit={handleSubmitDiscount}>
            <Stack spacing={2}>
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="Start date"
                value={moment(discountValues.startDate).format("YYYY-MM-DD")}
                onChange={(e) =>
                  setDiscountValues((prev) => ({ ...prev, startDate: e.target.value }))
                }
                inputProps={{
                  min: moment().format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                inputRef={discountStartInputRef}
                onClick={() => discountStartInputRef.current.showPicker()}
                InputProps={startIcon}
              />
              <CssTextField
                fullWidth
                required
                size="small"
                type="date"
                label="End date"
                value={moment(discountValues.endDate).format("YYYY-MM-DD")}
                onChange={(e) =>
                  setDiscountValues((prev) => ({ ...prev, endDate: e.target.value }))
                }
                inputProps={{
                  min: moment(discountValues.startDate).format("YYYY-MM-DD"),
                  max: moment().add(720, "days").format("YYYY-MM-DD"),
                }}
                inputRef={discountEndInputRef}
                onClick={() => discountEndInputRef.current.showPicker()}
                InputProps={startIcon}
              />

              <RadioGroup
                row
                value={discountValues.mode}
                onChange={(e) =>
                  setDiscountValues((prev) => ({ ...prev, mode: e.target.value }))
                }
              >
                <FormControlLabel
                  value="percentage"
                  control={<Radio size="small" />}
                  label="Percentage"
                />
                <FormControlLabel
                  value="fixed"
                  control={<Radio size="small" />}
                  label="Fixed Rate"
                />
              </RadioGroup>

              {discountValues.mode === "percentage" ? (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Percentage discount"
                  value={discountValues.discountValue}
                  inputProps={{ min: 0, max: 100 }}
                  onChange={(e) =>
                    setDiscountValues((prev) => ({
                      ...prev,
                      discountValue: e.target.value,
                    }))
                  }
                />
              ) : (
                <CssTextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="Fixed rate"
                  value={discountValues.fixedRate}
                  onChange={(e) =>
                    setDiscountValues((prev) => ({ ...prev, fixedRate: e.target.value }))
                  }
                />
              )}

              <Button fullWidth type="submit" color="secondary" variant="contained">
                {isSubmittingDiscount ? (
                  <CircularProgress color="inherit" size={22} />
                ) : (
                  <b>{editingDiscountId ? "Update" : "Submit"}</b>
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}

export default RoomtypeBookingEngine;
